import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Paper, 
  Button, 
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import PieChartIcon from '@mui/icons-material/PieChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import FunctionsIcon from '@mui/icons-material/Functions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';

// Beispieldaten für Analysepläne
const analysisPlans = [
  {
    id: 'descriptive',
    title: 'Deskriptive Statistik',
    description: 'Grundlegende statistische Analyse der ausgewählten Variablen',
    icon: <BarChartIcon />,
    details: 'Berechnung von Mittelwert, Median, Standardabweichung, Minimum, Maximum und Quartilen für alle numerischen Variablen. Häufigkeitsverteilungen für kategoriale Variablen.',
    options: [
      { id: 'mean', label: 'Mittelwert', default: true },
      { id: 'median', label: 'Median', default: true },
      { id: 'std', label: 'Standardabweichung', default: true },
      { id: 'min_max', label: 'Minimum und Maximum', default: true },
      { id: 'quartiles', label: 'Quartile', default: false },
      { id: 'histograms', label: 'Histogramme', default: true },
      { id: 'boxplots', label: 'Box-Plots', default: false }
    ]
  },
  {
    id: 'correlation',
    title: 'Korrelationsanalyse',
    description: 'Untersuchung der Beziehungen zwischen Variablen',
    icon: <TimelineIcon />,
    details: 'Berechnung von Pearson- oder Spearman-Korrelationskoeffizienten zwischen allen Paaren numerischer Variablen. Visualisierung durch Korrelationsmatrizen und Streudiagramme.',
    options: [
      { id: 'pearson', label: 'Pearson-Korrelation', default: true },
      { id: 'spearman', label: 'Spearman-Korrelation', default: false },
      { id: 'correlation_matrix', label: 'Korrelationsmatrix', default: true },
      { id: 'scatterplots', label: 'Streudiagramme', default: true }
    ]
  },
  {
    id: 'regression',
    title: 'Regressionsanalyse',
    description: 'Modellierung von Zusammenhängen zwischen Variablen',
    icon: <TrendingUpIcon />,
    details: 'Durchführung linearer oder logistischer Regressionsanalysen, um Beziehungen zwischen einer abhängigen Variable und einer oder mehreren unabhängigen Variablen zu modellieren.',
    options: [
      { id: 'linear', label: 'Lineare Regression', default: true },
      { id: 'logistic', label: 'Logistische Regression', default: false },
      { id: 'multivariate', label: 'Multivariate Regression', default: false },
      { id: 'residual_analysis', label: 'Residuenanalyse', default: true }
    ]
  },
  {
    id: 'comparison',
    title: 'Gruppenvergleich',
    description: 'Vergleich von Variablen zwischen verschiedenen Gruppen',
    icon: <CompareArrowsIcon />,
    details: 'Durchführung von t-Tests, ANOVA oder nicht-parametrischen Tests, um Unterschiede in Variablen zwischen verschiedenen Gruppen zu identifizieren.',
    options: [
      { id: 't_test', label: 't-Test', default: true },
      { id: 'anova', label: 'ANOVA', default: false },
      { id: 'mann_whitney', label: 'Mann-Whitney-U-Test', default: false },
      { id: 'kruskal_wallis', label: 'Kruskal-Wallis-Test', default: false }
    ]
  },
  {
    id: 'clustering',
    title: 'Clusteranalyse',
    description: 'Identifikation von Mustern und Gruppierungen in den Daten',
    icon: <BubbleChartIcon />,
    details: 'Anwendung von k-Means, hierarchischem Clustering oder anderen Clustering-Algorithmen, um natürliche Gruppierungen in den Daten zu identifizieren.',
    options: [
      { id: 'kmeans', label: 'K-Means Clustering', default: true },
      { id: 'hierarchical', label: 'Hierarchisches Clustering', default: false },
      { id: 'dbscan', label: 'DBSCAN', default: false },
      { id: 'cluster_validation', label: 'Cluster-Validierung', default: true }
    ]
  }
];

const AnalysisPlan = ({ onSaveAnalysisPlan, cohort, variables }) => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedOptions, setSelectedOptions] = useState({});
  const [customAnalysisText, setCustomAnalysisText] = useState('');
  const [analysisName, setAnalysisName] = useState('');
  const [error, setError] = useState('');

  // Initialisierung der ausgewählten Optionen für jeden Analyseplan
  React.useEffect(() => {
    const initialOptions = {};
    analysisPlans.forEach(plan => {
      initialOptions[plan.id] = plan.options
        .filter(option => option.default)
        .map(option => option.id);
    });
    setSelectedOptions(initialOptions);
  }, []);

  // Ändern der ausgewählten Optionen
  const handleOptionChange = (planId, optionId, checked) => {
    setSelectedOptions(prev => {
      const options = [...(prev[planId] || [])];
      if (checked) {
        if (!options.includes(optionId)) {
          options.push(optionId);
        }
      } else {
        const index = options.indexOf(optionId);
        if (index !== -1) {
          options.splice(index, 1);
        }
      }
      return { ...prev, [planId]: options };
    });
  };

  // Speichern des Analyseplans
  const saveAnalysisPlan = () => {
    if (!analysisName.trim()) {
      setError('Bitte geben Sie einen Namen für die Analyse ein');
      return;
    }

    if (!selectedPlan) {
      setError('Bitte wählen Sie einen Analyseplan aus');
      return;
    }

    const plan = analysisPlans.find(p => p.id === selectedPlan);
    const options = selectedOptions[selectedPlan] || [];

    const analysisPlan = {
      name: analysisName,
      planId: selectedPlan,
      planTitle: plan.title,
      options: options,
      customAnalysis: customAnalysisText
    };

    onSaveAnalysisPlan(analysisPlan);
    setError('');
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
        Schritt 3: Analyseplan auswählen
        <Tooltip title="Wählen Sie den gewünschten Analyseplan für Ihre Daten">
          <IconButton size="small">
            <HelpOutlineIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Wählen Sie einen Analyseplan aus, der auf Ihre Kohorte und Variablen angewendet werden soll.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Name der Analyse
              </Typography>
              <TextField
                fullWidth
                label="Name der Analyse"
                variant="outlined"
                value={analysisName}
                onChange={(e) => setAnalysisName(e.target.value)}
                placeholder="z.B. Diabetes-Risikofaktoren-Analyse"
                sx={{ mb: 2 }}
              />
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                Analyseplan auswählen
                <Tooltip title="Wählen Sie den Typ der statistischen Analyse">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              </Typography>
              
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup
                  aria-label="analysis-plan"
                  name="analysis-plan"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                >
                  <Grid container spacing={2}>
                    {analysisPlans.map((plan) => (
                      <Grid item xs={12} sm={6} key={plan.id}>
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            borderColor: selectedPlan === plan.id ? 'primary.main' : 'divider',
                            bgcolor: selectedPlan === plan.id ? 'rgba(255, 122, 0, 0.05)' : 'transparent',
                            transition: 'all 0.3s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }
                          }}
                        >
                          <FormControlLabel
                            value={plan.id}
                            control={<Radio color="primary" />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ color: 'primary.main' }}>
                                  {plan.icon}
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {plan.title}
                                </Typography>
                              </Box>
                            }
                            sx={{ width: '100%', m: 0 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                            {plan.description}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </RadioGroup>
              </FormControl>
              
              {selectedPlan && (
                <Box sx={{ mt: 3 }}>
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Analyseoptionen
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" paragraph>
                        {analysisPlans.find(p => p.id === selectedPlan)?.details}
                      </Typography>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Verfügbare Optionen:
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {analysisPlans.find(p => p.id === selectedPlan)?.options.map((option) => (
                          <Grid item xs={12} sm={6} key={option.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={(selectedOptions[selectedPlan] || []).includes(option.id)}
                                  onChange={(e) => handleOptionChange(selectedPlan, option.id, e.target.checked)}
                                  color="primary"
                                />
                              }
                              label={option.label}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel2a-content"
                      id="panel2a-header"
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Benutzerdefinierte Analyseanweisungen (optional)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" paragraph>
                        Geben Sie hier zusätzliche Anweisungen oder Anforderungen für Ihre Analyse ein.
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Benutzerdefinierte Analyseanweisungen"
                        variant="outlined"
                        value={customAnalysisText}
                        onChange={(e) => setCustomAnalysisText(e.target.value)}
                        placeholder="z.B. Bitte führen Sie eine zusätzliche Subgruppenanalyse für Patienten über 65 Jahre durch."
                      />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={saveAnalysisPlan}
                  disabled={!analysisName || !selectedPlan}
                >
                  Analyseplan speichern
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Zusammenfassung
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Ausgewählte Kohorte:
              </Typography>
              {cohort ? (
                <Chip
                  label={cohort.name}
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                  Keine Kohorte ausgewählt
                </Typography>
              )}
              
              <Typography variant="subtitle2" gutterBottom>
                Ausgewählte Variablen:
              </Typography>
              {variables && variables.variables && variables.variables.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`${variables.name} (${variables.variables.length} Variablen)`}
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {variables.variables.slice(0, 3).map(v => v.id).join(', ')}
                    {variables.variables.length > 3 && ` und ${variables.variables.length - 3} weitere`}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                  Keine Variablen ausgewählt
                </Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Hilfe & Tipps
              </Typography>
              
              <List dense>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <BarChartIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Deskriptive Statistik" 
                    secondary="Ideal für einen ersten Überblick über Ihre Daten"
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <TimelineIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Korrelationsanalyse" 
                    secondary="Untersucht Beziehungen zwischen Variablen"
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <TrendingUpIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Regressionsanalyse" 
                    secondary="Modelliert Zusammenhänge und Vorhersagen"
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CompareArrowsIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Gruppenvergleich" 
                    secondary="Vergleicht Variablen zwischen verschiedenen Gruppen"
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <BubbleChartIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Clusteranalyse" 
                    secondary="Identifiziert natürliche Gruppierungen in den Daten"
                  />
                </ListItem>
              </List>
              
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Tipp:</strong> Wählen Sie den Analyseplan, der am besten zu Ihren Forschungsfragen passt. Bei Unsicherheit beginnen Sie mit der deskriptiven Statistik.
              </Typography>
            </CardContent>
          </Card>
          
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                Beispielvisualisierungen
                <Tooltip title="Beispiele für mögliche Visualisierungen">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              </Typography>
              
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon color="primary" />
                    Histogramm
                  </Typography>
                  <Box 
                    component="img" 
                    src="https://via.placeholder.com/300x150?text=Histogramm+Beispiel" 
                    alt="Histogramm Beispiel"
                    sx={{ width: '100%', borderRadius: 1 }}
                  />
                </Paper>
                
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon color="primary" />
                    Streudiagramm
                  </Typography>
                  <Box 
                    component="img" 
                    src="https://via.placeholder.com/300x150?text=Streudiagramm+Beispiel" 
                    alt="Streudiagramm Beispiel"
                    sx={{ width: '100%', borderRadius: 1 }}
                  />
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalysisPlan;
